import React, { useState } from 'react';
import {
  Box, Typography, Popover, Avatar, Button, Stack, Divider, IconButton, Chip, Alert
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { useDispatch } from 'react-redux';
import { handleInvitation, markAsRead } from '../../actions/notification';
import { startChat } from '../../actions/chat';
import { useNavigate } from 'react-router-dom';
import { nombreDe } from '../common/imparte';
import { TICKET_LINK_TYPES, rutaReserva, botonAviso } from '../../utils/avisos';
import moment from 'moment';
import 'moment/locale/es';

const BOOKING_TYPES = ['BOOKING_REQUEST', 'BOOKING_CONFIRMED', 'BOOKING_DECLINED', 'BOOKING_EXPIRED', 'BOOKING_CANCELLED',
  'BOOKING_MODIFICATION_REQUEST', 'BOOKING_MODIFICATION_ACCEPTED', 'BOOKING_MODIFICATION_REJECTED', 'BOOKING_MODIFIED'];

// Tipos cuyo contenido va en data.message (pagos, disputas, solicitudes…): no llevan la
// tarjeta de detalle de invitación (De / Servicio / Modalidad), solo el mensaje.
const MESSAGE_TYPES = ['BOOKING_AUTOCOMPLETADA', 'BOOKING_DISPUTA', 'BOOKING_DISPUTA_RESUELTA', 'BOOKING_ACLARACION',
  'FACTURA_EMITIDA', 'EXTRA_AGREGADO', 'EXTRA_ELIMINADO',
  'PAYMENT_CONFIRMED', 'PAYMENT_REFERENCE', 'PAYMENT_FAILED', 'PAYMENT_REFUNDED',
  'PAYMENT_RELEASED', 'PAYMENT_NO_CLABE', 'PAYMENT_ONBOARDING', 'PAYMENT_CHARGEBACK',
  'SOLICITUD_RECIBIDA', 'SOLICITUD_RESUELTA', 'SOLICITUD_NOVEDAD', 'SOLICITUD_NO_PROCEDE',
  'TICKET_RESPUESTA', 'TICKET_ACTUALIZADO', 'COTIZACION_SOLICITADA'];

// Botón al lugar exacto de un aviso que no trae el suyo: la reserva de la que habla o «Datos para pagos» (ver utils/avisos)
const BotonAlLugar = ({ notif, onIr }) => {
  const boton = botonAviso(notif);
  if (!boton) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Button variant="contained" size="small" fullWidth onClick={() => onIr(boton.ruta)}
        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}>
        {boton.texto}
      </Button>
    </Box>
  );
};

const NotificacionesMenu = ({
  user, auth, notiOpen, notifications, handleCloseNot, navigate: navigateProp
}) => {
  const dispatch = useDispatch();
  const navigateHook = useNavigate();
  const navigate = navigateProp || navigateHook;
  moment.locale('es');

  // Resultado de responder una invitación: se confirma lo que pasó (o por qué no se pudo)
  const [aviso, setAviso] = useState(null);

  const responder = async (id, accion) => {
    const res = await dispatch(handleInvitation(id, accion));
    setAviso(res.ok
      ? { tipo: 'success', texto: accion === 'ACCEPT' ? 'Aceptaste la invitación. Ya apareces como quien imparte el servicio.' : 'Rechazaste la invitación.' }
      : { tipo: 'error', texto: res.msg });
  };

  const onAccept = (id) => responder(id, 'ACCEPT');

  const onReject = (id) => responder(id, 'REJECT');

  const onMarkRead = (id) => {
    dispatch(markAsRead(id));
  };

  const open = Boolean(notiOpen);
  const id = open ? 'notificaciones-popover' : undefined;

  const unreadCount = notifications ? notifications.filter(n => !n.read).length : 0;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={notiOpen}
      onClose={handleCloseNot}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: { xs: '92vw', sm: 400 },
          maxWidth: '92vw',
          height: 550,
          maxHeight: '80vh',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          mt: 1.5,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: (t) => alpha(t.palette.secondary.main, 0.05), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <NotificationsNoneIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1 }}>
              Notificaciones
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {unreadCount} sin leer
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleCloseNot}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {aviso && (
        <Alert severity={aviso.tipo} onClose={() => setAviso(null)} sx={{ borderRadius: 0 }}>
          {aviso.texto}
        </Alert>
      )}

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {notifications && notifications.length > 0 ? (
          notifications.map((notif) => (
            <Box
              key={notif._id}
              onClick={() => { if (!notif.read) onMarkRead(notif._id); }}
              sx={{
                p: 2,
                borderBottom: 1, borderColor: 'sutil',
                bgcolor: notif.read ? 'transparent' : ((t) => alpha(t.palette.primary.main, 0.02)),
                position: 'relative',
                cursor: notif.read ? 'default' : 'pointer'
              }}
            >
              <Stack direction="row" spacing={2}>
                <Box>
                  <Avatar sx={{
                    bgcolor: notif.type === 'INVITATION_REJECTED' || notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_REJECTED' ? 'error.main'
                      : notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED' ? 'success.main'
                      : notif.type === 'BOOKING_MODIFICATION_REQUEST' ? 'warning.main'
                      : notif.type === 'BOOKING_MODIFIED' || notif.type === 'BOOKING_DISPUTA_RESUELTA' || notif.type === 'TICKET_RESPUESTA' || notif.type === 'TICKET_ACTUALIZADO' ? 'info.main'
                      : notif.type === 'BOOKING_DISPUTA' || notif.type === 'PAYMENT_FAILED' || notif.type === 'PAYMENT_CHARGEBACK' || notif.type === 'SOLICITUD_NO_PROCEDE' ? 'error.main'
                      : notif.type === 'BOOKING_AUTOCOMPLETADA' || notif.type === 'PAYMENT_CONFIRMED' || notif.type === 'PAYMENT_RELEASED' || notif.type === 'SOLICITUD_RECIBIDA' || notif.type === 'SOLICITUD_RESUELTA' ? 'success.main'
                      : notif.type === 'SOLICITUD_NOVEDAD' ? 'warning.main'
                      : notif.type === 'BOOKING_EXPIRED' ? 'text.secondary'
                      : 'primary.main',
                    width: 40,
                    height: 40
                  }}>
                    {notif.type === 'PAGE_INVITATION' ? <PersonAddIcon fontSize="small" />
                      : notif.type === 'BOOKING_REQUEST' ? <CalendarMonthIcon fontSize="small" />
                      : notif.type === 'BOOKING_CONFIRMED' ? <EventAvailableIcon fontSize="small" />
                      : notif.type === 'BOOKING_DECLINED' ? <EventBusyIcon fontSize="small" />
                      : notif.type === 'BOOKING_CANCELLED' ? <EventBusyIcon fontSize="small" />
                      : notif.type === 'BOOKING_MODIFICATION_REQUEST' ? <CalendarMonthIcon fontSize="small" />
                      : notif.type === 'BOOKING_MODIFICATION_ACCEPTED' ? <EventAvailableIcon fontSize="small" />
                      : notif.type === 'BOOKING_MODIFICATION_REJECTED' ? <EventBusyIcon fontSize="small" />
                      : notif.type === 'BOOKING_MODIFIED' ? <EventAvailableIcon fontSize="small" />
                      : notif.type === 'BOOKING_EXPIRED' ? <AccessTimeIcon fontSize="small" />
                      : notif.type === 'SOLICITUD_RECIBIDA' || notif.type === 'SOLICITUD_RESUELTA' ? <CheckCircleOutlineIcon fontSize="small" />
                      : notif.type === 'SOLICITUD_NOVEDAD' ? <WarningAmberIcon fontSize="small" />
                      : notif.type === 'SOLICITUD_NO_PROCEDE' ? <BlockIcon fontSize="small" />
                      : notif.type === 'TICKET_RESPUESTA' || notif.type === 'TICKET_ACTUALIZADO' ? <ChatBubbleOutlineOutlinedIcon fontSize="small" />
                      : notif.type === 'COTIZACION_SOLICITADA' ? <RequestQuoteOutlinedIcon fontSize="small" />
                      : <NotificationsNoneIcon fontSize="small" />}
                  </Avatar>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {notif.type === 'PAGE_INVITATION' ? 'Solicitud para ser facilitador'
                        : notif.type === 'INVITATION_ACCEPTED' ? '¡Invitación Aceptada!'
                        : notif.type === 'INVITATION_REJECTED' ? 'Invitación Rechazada'
                        : notif.type === 'BOOKING_REQUEST' ? 'Nueva solicitud de reserva'
                        : notif.type === 'BOOKING_CONFIRMED' ? '¡Reserva confirmada!'
                        : notif.type === 'BOOKING_DECLINED' ? 'Reserva declinada'
                        : notif.type === 'BOOKING_CANCELLED' ? 'Reserva cancelada por el cliente'
                        : notif.type === 'BOOKING_MODIFICATION_REQUEST' ? 'Solicitud de cambio de fecha'
                        : notif.type === 'BOOKING_MODIFICATION_ACCEPTED' ? 'Cambio de fecha aceptado'
                        : notif.type === 'BOOKING_MODIFICATION_REJECTED' ? 'Cambio de fecha rechazado'
                        : notif.type === 'BOOKING_MODIFIED' ? 'Fecha de reserva actualizada'
                        : notif.type === 'BOOKING_EXPIRED' ? 'Reserva caducada'
                        : notif.type === 'BOOKING_AUTOCOMPLETADA' ? 'Servicio completado'
                        : notif.type === 'BOOKING_DISPUTA' ? 'Problema reportado'
                        : notif.type === 'BOOKING_DISPUTA_RESUELTA' ? 'Disputa resuelta'
                        : notif.type === 'SOLICITUD_RECIBIDA' ? 'Solicitud recibida'
                        : notif.type === 'SOLICITUD_RESUELTA' ? 'Solicitud resuelta'
                        : notif.type === 'SOLICITUD_NOVEDAD' ? 'Noticias sobre tu solicitud'
                        : notif.type === 'SOLICITUD_NO_PROCEDE' ? 'Tu solicitud no procedió'
                        : notif.type === 'TICKET_RESPUESTA' ? 'Respuesta a tu ticket'
                        : notif.type === 'TICKET_ACTUALIZADO' ? 'Actualización de tu ticket'
                        : notif.type === 'COTIZACION_SOLICITADA' ? 'Solicitud de cotización'
                        : notif.type === 'PAYMENT_CONFIRMED' ? 'Pago confirmado'
                        : notif.type === 'PAYMENT_REFERENCE' ? 'Referencia de pago'
                        : notif.type === 'PAYMENT_FAILED' ? 'Problema con un pago'
                        : notif.type === 'PAYMENT_REFUNDED' ? 'Reembolso realizado'
                        : notif.type === 'PAYMENT_RELEASED' ? 'Pago transferido'
                        : notif.type === 'PAYMENT_NO_CLABE' ? 'Registra tu CLABE'
                        : notif.type === 'PAYMENT_ONBOARDING' ? 'Conecta tu cuenta'
                        : notif.type === 'PAYMENT_CHARGEBACK' ? 'Contracargo en revisión'
                        : notif.type === 'EXTRA_AGREGADO' ? 'Extra agregado al servicio'
                        : notif.type === 'EXTRA_ELIMINADO' ? 'Extra eliminado'
                        : 'Notificación'}
                    </Typography>
                    {!notif.read && (
                      <Box sx={{ width: 8, height: 8, bgcolor: 'primary.main', borderRadius: '50%', mt: 0.5, flexShrink: 0 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {notif.type === 'PAGE_INVITATION' ? (notif.data?.serviceName
                      ? `${nombreDe(notif.sender) || 'Alguien'} te invita a impartir un servicio en su página. No se podrá contratar hasta que respondas.`
                      : `${nombreDe(notif.sender) || 'Alguien'} te invita a formar parte de su página.`)
                      : notif.type === 'INVITATION_ACCEPTED' ? (notif.data?.message || `${notif.data?.userName || 'Alguien'} ha aceptado unirse a tu página.`)
                      : notif.type === 'INVITATION_REJECTED' ? (notif.data?.message || `${notif.data?.userName || 'Alguien'} ha declinado la invitación.`)
                      : notif.type === 'BOOKING_REQUEST' ? `${notif.data?.userName} quiere reservar tu servicio.`
                      : notif.type === 'BOOKING_CONFIRMED' ? `${notif.data?.userName} confirmó tu reserva.`
                      : notif.type === 'BOOKING_DECLINED' ? `${notif.data?.userName} declinó tu solicitud.`
                      : notif.type === 'BOOKING_CANCELLED' ? `${notif.data?.userName} canceló su reserva.`
                      : notif.type === 'BOOKING_MODIFICATION_REQUEST' ? `${notif.data?.userName} quiere cambiar la fecha de la reserva.`
                      : notif.type === 'BOOKING_MODIFICATION_ACCEPTED' ? (notif.data?.confirmada
                        ? `${notif.data?.userName} aceptó tu propuesta y la reserva quedó confirmada.`
                        : `${notif.data?.userName} aceptó tu solicitud de cambio.`)
                      : notif.type === 'BOOKING_MODIFICATION_REJECTED' ? `${notif.data?.userName} rechazó tu solicitud de cambio.`
                      : notif.type === 'BOOKING_MODIFIED' ? `${notif.data?.userName} cambió la fecha de su reserva pendiente.`
                      : notif.type === 'BOOKING_EXPIRED' ? 'El proveedor no respondió a tiempo. Tu reserva fue cancelada automáticamente.'
                      : (notif.data?.message || '')}
                  </Typography>

                  {/* Booking detail card */}
                  {BOOKING_TYPES.includes(notif.type) && (
                    <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: '8px', mb: 1.5 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                          <Box component="span" sx={{ color: 'text.secondary' }}>Servicio:</Box>
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{notif.data?.serviceName}</Box>
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                          <Box component="span" sx={{ color: 'text.secondary' }}>Fecha:</Box>
                          <Box component="span" sx={{ color: 'primary.main' }}>{notif.data?.fecha}</Box>
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                          <Box component="span" sx={{ color: 'text.secondary' }}>Hora:</Box>
                          <Box component="span" sx={{ color: 'primary.main' }}>{notif.data?.hora} hrs</Box>
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  {/* Invitation detail card */}
                  {!BOOKING_TYPES.includes(notif.type) && !MESSAGE_TYPES.includes(notif.type) && notif.type !== 'SYSTEM' && (
                    <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: '8px', mb: 1.5 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                          <Box component="span" sx={{ color: 'text.secondary' }}>Página:</Box>
                          <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{notif.data?.pageName}</Box>
                        </Typography>
                        {notif.data?.serviceName && (
                          <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                            <Box component="span" sx={{ color: 'text.secondary' }}>Servicio:</Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{notif.data.serviceName}</Box>
                          </Typography>
                        )}
                        {notif.type === 'PAGE_INVITATION' && notif.data?.modality && (
                          <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                            <Box component="span" sx={{ color: 'text.secondary' }}>Modalidad:</Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{notif.data.modality}</Box>
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  )}

                  {/* Booking request / modification request actions */}
                  {(notif.type === 'BOOKING_REQUEST' || notif.type === 'BOOKING_MODIFICATION_REQUEST' || notif.type === 'BOOKING_MODIFIED') && (
                    <Box sx={{ mb: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => { handleCloseNot(); navigate(rutaReserva(notif)); }}
                        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}
                      >
                        {notif.type === 'BOOKING_MODIFIED' ? 'Ver reserva' : 'Ver solicitud'}
                      </Button>
                    </Box>
                  )}

                  {/* Booking confirmed/declined/expired chip */}
                  {(notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_EXPIRED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') && (
                    <Box sx={{ mb: 1 }}>
                      <Chip
                        label={
                          notif.type === 'BOOKING_CONFIRMED' ? 'CONFIRMADA'
                          : notif.type === 'BOOKING_DECLINED' ? 'DECLINADA'
                          : notif.type === 'BOOKING_CANCELLED' ? 'CANCELADA'
                          : notif.type === 'BOOKING_MODIFICATION_ACCEPTED' ? 'CAMBIO ACEPTADO'
                          : notif.type === 'BOOKING_MODIFICATION_REJECTED' ? 'CAMBIO RECHAZADO'
                          : 'CADUCADA'
                        }
                        size="small"
                        sx={{
                          bgcolor: (notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED') ? ((t) => alpha(t.palette.success.main, 0.1))
                            : (notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') ? ((t) => alpha(t.palette.error.main, 0.1))
                            : 'grey.100',
                          color: (notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED') ? 'success.oscuro'
                            : (notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') ? 'error.oscuro'
                            : 'text.secondary',
                          fontWeight: 'bold', borderRadius: '6px'
                        }}
                      />
                    </Box>
                  )}

                  {/* Page invitation actions */}
                  {notif.type === 'PAGE_INVITATION' && notif.status === 'PENDING' ? (
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Button variant="contained" size="small" fullWidth onClick={() => onAccept(notif._id)}
                        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}>
                        Aceptar
                      </Button>
                      <Button variant="contained" size="small" fullWidth color="success"
                        onClick={() => { handleCloseNot(); navigate('/user/colaboraciones'); }}
                        sx={{ color: 'white', textTransform: 'none', borderRadius: '8px', fontSize: '11px' }}>
                        Ver detalles
                      </Button>
                      <Button variant="contained" color="error" size="small" fullWidth onClick={() => onReject(notif._id)}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '11px' }}>
                        Rechazar
                      </Button>
                    </Box>
                  ) : notif.type === 'PAGE_INVITATION' ? (
                    <Box sx={{ mb: 1 }}>
                      {/* CANCELLED: la invitación dejó de valer antes de responderla (se eligió a otra persona, se quitó el servicio…) */}
                      <Chip
                        label={notif.status === 'ACCEPTED' ? 'ACEPTADA' : notif.status === 'CANCELLED' ? 'YA NO DISPONIBLE' : 'RECHAZADA'}
                        size="small"
                        sx={{
                          bgcolor: notif.status === 'ACCEPTED' ? ((t) => alpha(t.palette.success.main, 0.1)) : notif.status === 'CANCELLED' ? 'sutil' : ((t) => alpha(t.palette.error.main, 0.1)),
                          color: notif.status === 'ACCEPTED' ? 'success.oscuro' : notif.status === 'CANCELLED' ? 'text.secondary' : 'error.oscuro',
                          fontWeight: 'bold', borderRadius: '6px'
                        }}
                      />
                    </Box>
                  ) : null}

                  {/* Al dueño: si rechazaron (o dejaron) un servicio, va directo a la página a decidir quién lo imparte */}
                  {notif.type === 'INVITATION_REJECTED' && notif.data?.pageId && (
                    <Box sx={{ mb: 1 }}>
                      <Button variant="contained" size="small" fullWidth
                        onClick={() => { handleCloseNot(); navigate(`/user/crear-servicio/1?edit=${notif.data.pageId}&type=group`); }}
                        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}>
                        Elegir quién lo imparte
                      </Button>
                    </Box>
                  )}

                  {/* Alguien pidió cotizar: abre esa conversación para responderle */}
                  {notif.type === 'COTIZACION_SOLICITADA' && notif.sender?._id && notif.data?.relatedId && (
                    <Box sx={{ mb: 1 }}>
                      <Button variant="contained" size="small" fullWidth
                        onClick={() => { handleCloseNot(); dispatch(startChat(notif.sender._id, notif.data.relatedId, notif.data.relatedType)); }}
                        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}>
                        Responder
                      </Button>
                    </Box>
                  )}

                  {TICKET_LINK_TYPES.includes(notif.type) && (
                    <Box sx={{ mb: 1 }}>
                      <Button variant="contained" size="small" fullWidth
                        onClick={() => { handleCloseNot(); navigate(notif.data?.ticketId ? `/user/tickets?id=${notif.data.ticketId}` : '/user/tickets'); }}
                        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '8px', fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}>
                        Ver ticket
                      </Button>
                    </Box>
                  )}

                  <BotonAlLugar notif={notif} onIr={(ruta) => { handleCloseNot(); navigate(ruta); }} />

                  <Typography variant="caption" color="text.secondary">
                    hace {moment(notif.date).fromNow(true)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tienes nuevas notificaciones
            </Typography>
          </Box>
        )}
      </Box>

      {notifications && notifications.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Button
              variant="text"
              size="small"
              onClick={() => { handleCloseNot(); navigate('/user/notificaciones'); }}
              sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 'bold' }}
            >
              Ver todas las notificaciones
            </Button>
          </Box>
        </>
      )}
    </Popover>
  );
};

export default NotificacionesMenu;